
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithEmailAndPassword, updatePassword } from 'firebase/auth';
import { auth } from '../firebase';
import { Button, Input, Card } from '../components/ui';

const SetupAccount = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'verifying' | 'set-password'>('verifying');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const init = async () => {
      const email = searchParams.get('e');
      const code = searchParams.get('c'); // Base64 encoded temp password

      if (!email || !code) {
        setError("Invalid invitation link.");
        setLoading(false);
        return;
      }

      try {
        // Decode temp password
        const tempPassword = atob(code);
        
        // Attempt to sign in
        await signInWithEmailAndPassword(auth, email, tempPassword);
        setStep('set-password');
      } catch (err: any) {
        console.error(err);
        if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
             setError("This invitation link has expired or has already been used. Please log in normally.");
        } else {
             setError("Error verifying link: " + err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [searchParams]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      alert("Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      alert("Password must be at least 6 characters");
      return;
    }

    setIsSaving(true);
    try {
      if (auth.currentUser) {
        await updatePassword(auth.currentUser, newPassword);
        alert("Password set successfully! Redirecting to dashboard...");
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error(err);
      alert("Error setting password: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
      <Card className="w-full max-w-md p-8 shadow-xl">
        <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-slate-900">Account Setup</h1>
            <p className="text-slate-500 text-sm mt-1">CLASS Training Hub</p>
        </div>

        {loading && <div className="text-center text-slate-500 py-8">Verifying invitation...</div>}

        {error && (
            <div className="text-center">
                <div className="bg-rose-50 text-rose-700 p-4 rounded-lg mb-4 text-sm">
                    {error}
                </div>
                <Button onClick={() => navigate('/')} variant="outline" className="w-full">Go to Login</Button>
            </div>
        )}

        {!loading && !error && step === 'set-password' && (
            <form onSubmit={handleSave} className="space-y-6 animate-fade-in">
                <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-lg text-sm text-emerald-800 mb-2 flex gap-3 items-start">
                    <span className="text-lg">✅</span>
                    <div>
                        <strong>Verified successfully.</strong>
                        <p>Please create your secure password to activate your account.</p>
                    </div>
                </div>
                
                <Input 
                    label="Create Password" 
                    type="password" 
                    value={newPassword} 
                    onChange={e => setNewPassword(e.target.value)} 
                    placeholder="Min 6 characters"
                    required
                />
                <Input 
                    label="Confirm Password" 
                    type="password" 
                    value={confirmPassword} 
                    onChange={e => setConfirmPassword(e.target.value)} 
                    placeholder="Repeat password"
                    required
                />
                
                <Button type="submit" className="w-full py-3 shadow-lg shadow-indigo-200" disabled={isSaving}>
                    {isSaving ? "Activating..." : "Set Password & Enter Portal"}
                </Button>
            </form>
        )}
      </Card>
    </div>
  );
};

export default SetupAccount;
